import { useState, useEffect, useCallback } from "react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, Users, Copy, Check, Hash, Eye, AlertTriangle, FlaskConical } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";

// ---- SLID Copy Button ----
function SlidBadge({ slid }) {
  const [copied, setCopied] = useState(false);
  if (!slid) return <span className="text-slate-300 text-xs">—</span>;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(slid).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold px-2 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors group"
      title="Click to copy SLID"
      data-testid={`slid-badge-${slid}`}
    >
      {slid}
      {copied ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
    </button>
  );
}

// ============ COMPANIES TAB ============

export function CompaniesTab({ companies, campaigns, publishers, onReload, api = adminApi, isProduct = false, previewBase = "/admin/preview" }) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const emptyForm = {
    company_name: "", company_type: "sl_partner", campaign_ids: [],
    business_model: "full_passback", rev_share_pct: 50, fixed_price_per_device: 0.01, effective_date: "",
    ad_business_model: "", ad_rev_share_pct: 0, ad_fixed_price_per_device: 0.01,
    payout_currency: "USD", slid: "",
  };
  const [form, setForm] = useState(emptyForm);

  const openCreate = () => { setForm(emptyForm); setEditMode(false); setEditId(null); setDialogOpen(true); };
  const openEdit = (c) => {
    setForm({
      company_name: c.company_name, company_type: c.company_type || "sl_partner", campaign_ids: c.campaign_ids || [],
      business_model: c.business_model || "full_passback",
      rev_share_pct: c.rev_share_pct ?? 50, fixed_price_per_device: c.fixed_price_per_device ?? 0.01,
      effective_date: c.effective_date || "",
      ad_business_model: c.ad_business_model || "",
      ad_rev_share_pct: c.ad_rev_share_pct ?? 0, ad_fixed_price_per_device: c.ad_fixed_price_per_device ?? 0.01,
      payout_currency: c.payout_currency || "USD",
      slid: c.slid || "",
    });
    setEditMode(true); setEditId(c.company_id); setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.company_name.trim()) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        company_name: form.company_name.trim(),
        company_type: form.company_type,
        campaign_ids: form.campaign_ids,
        business_model: form.business_model,
        rev_share_pct: parseFloat(form.rev_share_pct) || 0,
        fixed_price_per_device: parseFloat(form.fixed_price_per_device) || 0,
        effective_date: form.effective_date || null,
        ad_business_model: form.ad_business_model || null,
        ad_rev_share_pct: parseFloat(form.ad_rev_share_pct) || 0,
        ad_fixed_price_per_device: parseFloat(form.ad_fixed_price_per_device) || 0,
        payout_currency: form.payout_currency || "USD",
      };
      const res = editMode ? await api.updateCompany(editId, payload) : await api.createCompany(payload);
      toast.success(editMode ? "Company updated" : "Company created");
      (res.data?.key_conflicts || []).forEach((k) => toast.warning(`Key "${k.key}" is also assigned to ${k.company_name}`));
      setDialogOpen(false); onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save company"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete company "${name}"?`)) return;
    try { await api.deleteCompany(id); toast.success("Company deleted"); onReload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const toggleCampaign = (cid) => {
    setForm(f => ({
      ...f, campaign_ids: f.campaign_ids.includes(cid)
        ? f.campaign_ids.filter(x => x !== cid)
        : [...f.campaign_ids, cid]
    }));
  };

  const conflicts = form.campaign_ids
    .map((k) => ({ key: k, owner: companies.find((c) => c.company_id !== editId && (c.campaign_ids || []).includes(k)) }))
    .filter((x) => x.owner);

  const bizModelLabel = (m) => ({ full_passback: "Full Passback", percentage_share: "% Share", fixed_payout: "Fixed Payout" }[m] || m || "—");

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Companies</h2>
          <p className="text-xs text-slate-400 mt-0.5">{isProduct ? "View every company. You can create new companies and edit the ones the Product team created." : "Manage company identifiers and revenue share configuration"}</p>
        </div>
        <Button onClick={openCreate} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="create-company-btn">
          <Plus size={14} className="mr-1" /> Add Company
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80 border-b border-slate-200">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Company</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">SLID</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Attribution Keys</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Data Model</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Ad Model</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Effective Date</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400">No companies yet. Create one to get started.</TableCell></TableRow>
            ) : companies.map(c => (
              <TableRow key={c.company_id} className="hover:bg-slate-50/50" data-testid={`company-row-${c.company_id}`}>
                <TableCell className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    <Building2 size={14} className={c.company_type === "content_partner" ? "text-fuchsia-500" : "text-blue-500"} />
                    <span className="text-sm font-medium text-slate-900">{c.company_name}</span>
                    {c.company_type === "content_partner" && <Badge variant="secondary" className="text-[10px] bg-fuchsia-50 text-fuchsia-700">SLCP</Badge>}
                    {c.created_by_product && <Badge variant="secondary" className="text-[10px] bg-teal-50 text-teal-700 gap-1" data-testid={`product-created-badge-${c.company_id}`}><FlaskConical size={10} />Product-created</Badge>}
                    <Badge variant="secondary" className={`text-[10px] ${(c.payout_currency || "USD") === "INR" ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-600"}`} data-testid={`company-currency-badge-${c.company_id}`}>{(c.payout_currency || "USD") === "INR" ? "₹ INR" : "$ USD"}</Badge>
                  </div>
                </TableCell>
                <TableCell className="py-2 px-3">
                  {c.company_type === "content_partner"
                    ? <span className="text-[10px] text-slate-400 italic">N/A (SLCP)</span>
                    : <SlidBadge slid={c.slid} />
                  }
                </TableCell>
                <TableCell className="py-2 px-3">
                  <div className="flex flex-wrap gap-1">
                    {(c.campaign_ids || []).map(id => (
                      <Badge key={id} variant="secondary" className={`text-xs ${c.company_type === "content_partner" ? "bg-fuchsia-50 text-fuchsia-700" : "bg-blue-50 text-blue-700"}`}>{id}</Badge>
                    ))}
                    {(!c.campaign_ids || c.campaign_ids.length === 0) && <span className="text-xs text-slate-300">None</span>}
                  </div>
                </TableCell>
                <TableCell className="py-2 px-3 text-sm text-slate-600">
                  {c.company_type === "content_partner" ? (
                    <span>SLCP Share <span className="text-xs text-slate-400 ml-1">({c.rev_share_pct}%)</span></span>
                  ) : (
                  <>
                  {bizModelLabel(c.business_model)}
                  {c.business_model === "percentage_share" && <span className="text-xs text-slate-400 ml-1">({c.rev_share_pct}%)</span>}
                  {c.business_model === "fixed_payout" && <span className="text-xs text-slate-400 ml-1">({(c.payout_currency || "USD") === "INR" ? "₹" : "$"}{c.fixed_price_per_device}/device)</span>}
                  </>
                  )}
                </TableCell>
                <TableCell className="py-2 px-3 text-sm text-slate-600">
                  {c.ad_business_model ? bizModelLabel(c.ad_business_model) : <span className="text-slate-300">—</span>}
                  {c.ad_business_model === "percentage_share" && <span className="text-xs text-slate-400 ml-1">({c.ad_rev_share_pct}%)</span>}
                </TableCell>
                <TableCell className="py-2 px-3 text-sm text-slate-500">{c.effective_date || <span className="text-slate-300">—</span>}</TableCell>
                <TableCell className="py-2 px-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs text-blue-600 hover:bg-blue-50" onClick={() => navigate(`${previewBase}/${c.company_id}`)} data-testid={`view-as-partner-${c.company_id}`} title="View as partner">
                      <Eye size={14} className="mr-1" /> View as partner
                    </Button>
                    {(!isProduct || c.editable) && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-company-${c.company_id}`}>
                        <Pencil size={14} className="text-slate-400" />
                      </Button>
                    )}
                    {!isProduct && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(c.company_id, c.company_name)} data-testid={`delete-company-${c.company_id}`}>
                        <Trash2 size={14} className="text-red-400" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit Company" : "Add Company"}</DialogTitle>
            <DialogDescription>Configure company identifiers and revenue share settings</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Company Name</Label>
              <Input value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} placeholder="e.g. CVTE" data-testid="company-name-input" className="h-10 border-slate-200" />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Company Type</Label>
              <Select value={form.company_type} onValueChange={v => setForm({...form, company_type: v})}>
                <SelectTrigger className="h-10 border-slate-200" data-testid="company-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sl_partner">SilverLine Device Partner (SLDP)</SelectItem>
                  <SelectItem value="content_partner">SilverLine Content Partner (SLCP)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* SLID — shown only for SL partners; read-only in edit mode */}
            {form.company_type !== "content_partner" && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                  <Hash size={14} className="text-blue-500" /> SilverLine ID (SLID)
                </Label>
                {editMode ? (
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-3 py-2 rounded-lg flex-1" data-testid="company-slid-display">{form.slid || "—"}</span>
                    <Button type="button" variant="outline" size="sm" className="h-9 text-xs" onClick={() => { navigator.clipboard.writeText(form.slid || ""); toast.success("SLID copied"); }} data-testid="copy-slid-btn">
                      <Copy size={12} className="mr-1" /> Copy
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">A unique SLID will be auto-generated when the company is created. Embed it in your ad tag URL as a macro (e.g. <code className="font-mono text-blue-600">&amp;cd20=SLXXXX</code>) for attribution.</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Payout Currency</Label>
              <Select value={form.payout_currency} onValueChange={v => setForm({...form, payout_currency: v})}>
                <SelectTrigger className="h-10 border-slate-200" data-testid="company-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD ($)</SelectItem>
                  <SelectItem value="INR">INR (₹)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {form.payout_currency === "INR"
                  ? "Fixed-payout amounts are entered directly in ₹. Rev-share amounts stay in USD and convert to ₹ per-invoice at the payment-date bank rate."
                  : "Partner is billed and shown revenue in US Dollars."}
              </p>
            </div>

            {form.company_type === "content_partner" ? (
            <>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Aniview Publisher(s)</Label>
                {(() => {
                  const opts = Array.from(new Set([...(publishers || []), ...form.campaign_ids]));
                  return opts.length > 0 ? (
                    <div className="border border-slate-200 rounded-lg p-3 max-h-[160px] overflow-y-auto space-y-1" data-testid="cp-publisher-select">
                      {opts.map(p => (
                        <label key={p} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-2 py-1" data-testid={`cp-publisher-option-${p}`}>
                          <Checkbox checked={form.campaign_ids.includes(p)} onCheckedChange={() => toggleCampaign(p)} />
                          <span className="text-sm text-slate-700 font-mono">{p}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400">No Aniview publishers found. Sync FAST TV Ads data first.</p>
                  );
                })()}
                <p className="text-xs text-slate-500">{form.campaign_ids.length} publisher(s) selected · channels are auto-attributed from the data under these publishers.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">SLCP Revenue Share %</Label>
                <Input type="number" step="0.01" min="0" max="100" value={form.rev_share_pct} onChange={e => setForm({...form, rev_share_pct: e.target.value})} data-testid="cp-rev-share-pct" className="h-10 border-slate-200" />
                <p className="text-xs text-slate-500">SLCP earns (gross − ad server cost) × this %. The remainder is split with SilverLine Device Partners.</p>
              </div>
            </>
            ) : (
            <>
            {/* Attribution Keys */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Attribution Keys</Label>
              {campaigns.length > 0 ? (
                <div className="border border-slate-200 rounded-lg p-3 max-h-[140px] overflow-y-auto space-y-1">
                  {campaigns.map(c => (
                    <label key={c} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-2 py-1">
                      <Checkbox checked={form.campaign_ids.includes(c)} onCheckedChange={() => toggleCampaign(c)} />
                      <span className="text-sm text-slate-700">{c}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400">No attribution keys available. Upload data first.</p>
              )}
              <p className="text-xs text-slate-500">{form.campaign_ids.length} attribution key(s) selected</p>
            </div>

            {/* Data Revenue Config */}
            <div className="space-y-3 border border-slate-100 rounded-lg p-4 bg-slate-50/50">
              <h4 className="text-xs font-semibold uppercase text-slate-500 tracking-wider">Data Revenue Config</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Business Model</Label>
                  <Select value={form.business_model} onValueChange={v => setForm({...form, business_model: v})}>
                    <SelectTrigger className="h-9 text-sm" data-testid="company-biz-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full_passback">Full Passback</SelectItem>
                      <SelectItem value="percentage_share">Percentage Share</SelectItem>
                      <SelectItem value="fixed_payout">Fixed Payout</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.business_model === "percentage_share" && (
                  <div>
                    <Label className="text-xs text-slate-500">Rev Share %</Label>
                    <Input type="number" value={form.rev_share_pct} onChange={e => setForm({...form, rev_share_pct: e.target.value})} className="h-9 text-sm" data-testid="company-rev-share-pct" />
                  </div>
                )}
                {form.business_model === "fixed_payout" && (
                  <div>
                    <Label className="text-xs text-slate-500">Price/Device ({form.payout_currency === "INR" ? "₹" : "$"})</Label>
                    <Input type="number" step="0.001" value={form.fixed_price_per_device} onChange={e => setForm({...form, fixed_price_per_device: e.target.value})} className="h-9 text-sm" data-testid="company-fixed-price" />
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs text-slate-500">Effective Date</Label>
                <Input type="date" value={form.effective_date} onChange={e => setForm({...form, effective_date: e.target.value})} className="h-9 text-sm" />
              </div>
            </div>

            {/* Ad Revenue Config */}
            <div className="space-y-3 border border-slate-100 rounded-lg p-4 bg-slate-50/50">
              <h4 className="text-xs font-semibold uppercase text-slate-500 tracking-wider">Ad Revenue Config</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Ad Business Model</Label>
                  <Select value={form.ad_business_model || "none"} onValueChange={v => setForm({...form, ad_business_model: v === "none" ? "" : v})}>
                    <SelectTrigger className="h-9 text-sm" data-testid="company-ad-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not Configured</SelectItem>
                      <SelectItem value="full_passback">Full Passback</SelectItem>
                      <SelectItem value="percentage_share">Percentage Share</SelectItem>
                      <SelectItem value="fixed_payout">Fixed Payout</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.ad_business_model === "percentage_share" && (
                  <div>
                    <Label className="text-xs text-slate-500">Ad Rev Share %</Label>
                    <Input type="number" value={form.ad_rev_share_pct} onChange={e => setForm({...form, ad_rev_share_pct: e.target.value})} className="h-9 text-sm" data-testid="company-ad-rev-share" />
                  </div>
                )}
                {form.ad_business_model === "fixed_payout" && (
                  <div>
                    <Label className="text-xs text-slate-500">Price/Device ({form.payout_currency === "INR" ? "₹" : "$"})</Label>
                    <Input type="number" step="0.001" value={form.ad_fixed_price_per_device} onChange={e => setForm({...form, ad_fixed_price_per_device: e.target.value})} className="h-9 text-sm" />
                  </div>
                )}
              </div>
            </div>
            </>
            )}
          </div>
          {conflicts.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2" data-testid="key-conflict-warning">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
              <div>{conflicts.map((x) => <p key={x.key}>"{x.key}" belongs to <b>{x.owner.company_name}</b> — assigning it here also changes that company's attribution.</p>)}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-company-btn">
              {saving ? "Saving..." : editMode ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ USERS TAB ============

function UsersTab({ partners, companies, onReload }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = { partner_name: "", username: "", password: "", role: "partner", google_email: "", company_id: "" };
  const [form, setForm] = useState(emptyForm);

  const companyMap = {};
  for (const c of companies) companyMap[c.company_id] = c.company_name;

  const openCreate = () => { setForm(emptyForm); setEditMode(false); setEditId(null); setDialogOpen(true); };
  const openEdit = (p) => {
    setForm({
      partner_name: p.partner_name, username: p.username, password: "",
      role: p.role || "partner", google_email: p.google_email || "", company_id: p.company_id || "",
    });
    setEditMode(true); setEditId(p.partner_id); setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.partner_name.trim() || !form.username.trim()) { toast.error("Name and username are required"); return; }
    if (!editMode && !form.password) { toast.error("Password is required for new users"); return; }
    setSaving(true);
    try {
      if (editMode) {
        const updateData = {
          partner_name: form.partner_name.trim(),
          username: form.username.trim(),
          google_email: form.google_email || null,
          company_id: form.company_id || null,
        };
        if (form.password) updateData.password = form.password;
        await adminApi.updatePartner(editId, updateData);
        toast.success("User updated");
      } else {
        await adminApi.createPartner({
          partner_name: form.partner_name.trim(),
          username: form.username.trim(),
          password: form.password,
          role: form.role,
          google_email: form.google_email || null,
          company_id: form.company_id || null,
        });
        toast.success("User created");
      }
      setDialogOpen(false); onReload();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"?`)) return;
    try { await adminApi.deletePartner(id); toast.success("User deleted"); onReload(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Users</h2>
          <p className="text-xs text-slate-400 mt-0.5">Manage user accounts and link them to companies</p>
        </div>
        <Button onClick={openCreate} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="create-user-btn">
          <Plus size={14} className="mr-1" /> Add User
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80 border-b border-slate-200">
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Name</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Role</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Company</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Username</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3">Google Email</TableHead>
              <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider py-2 px-3 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {partners.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">No users yet.</TableCell></TableRow>
            ) : partners.map(p => (
              <TableRow key={p.partner_id} className="hover:bg-slate-50/50" data-testid={`user-row-${p.partner_id}`}>
                <TableCell className="py-2 px-3">
                  <div className="flex items-center gap-2">
                    <Users size={14} className="text-slate-400" />
                    <span className="text-sm font-medium text-slate-900">{p.partner_name}</span>
                  </div>
                </TableCell>
                <TableCell className="py-2 px-3">
                  <Badge variant={p.role === "user" || p.role === "product" ? "outline" : "default"} className={p.role === "product" ? "text-xs bg-violet-50 text-violet-700 border-violet-200" : p.role === "user" ? "text-xs" : "text-xs bg-emerald-50 text-emerald-700 border-emerald-200"}>
                    {p.role === "user" ? "Internal" : p.role === "product" ? "Product" : "Partner"}
                  </Badge>
                </TableCell>
                <TableCell className="py-2 px-3 text-sm">
                  {p.company_id && companyMap[p.company_id]
                    ? <span className="text-slate-700 flex items-center gap-1"><Building2 size={12} className="text-blue-400" /> {companyMap[p.company_id]}</span>
                    : <span className="text-slate-300">—</span>
                  }
                </TableCell>
                <TableCell className="py-2 px-3 text-sm text-slate-600">{p.username}</TableCell>
                <TableCell className="py-2 px-3 text-sm text-slate-500">{p.google_email || <span className="text-slate-300">—</span>}</TableCell>
                <TableCell className="py-2 px-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`edit-user-${p.partner_id}`}>
                      <Pencil size={14} className="text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(p.partner_id, p.partner_name)} data-testid={`delete-user-${p.partner_id}`}>
                      <Trash2 size={14} className="text-red-400" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{editMode ? "Edit User" : "Add User"}</DialogTitle>
            <DialogDescription>Configure user login and company assignment</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">Name</Label>
                <Input value={form.partner_name} onChange={e => setForm({...form, partner_name: e.target.value})} placeholder="Alice Zhang" data-testid="user-name-input" className="h-10 border-slate-200" />
              </div>
              {!editMode && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">Role</Label>
                  <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                    <SelectTrigger className="h-10" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="user">Internal User</SelectItem>
                      <SelectItem value="product">Product (learning)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Company assignment */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Company</Label>
              <Select value={form.company_id || "none"} onValueChange={v => setForm({...form, company_id: v === "none" ? "" : v})}>
                <SelectTrigger className="h-10" data-testid="user-company-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No company</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">Revenue config and identifiers come from the company</p>
            </div>

            {/* Login credentials */}
            <div className="space-y-3 border border-slate-100 rounded-lg p-4 bg-slate-50/50">
              <h4 className="text-xs font-semibold uppercase text-slate-500 tracking-wider">Login Credentials</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Username</Label>
                  <Input value={form.username} onChange={e => setForm({...form, username: e.target.value})} placeholder="username" data-testid="user-username-input" className="h-9 text-sm border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Password{editMode && " (optional)"}</Label>
                  <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder={editMode ? "Leave blank to keep" : "Required"} data-testid="user-password-input" className="h-9 text-sm border-slate-200" />
                </div>
              </div>
            </div>

            {/* Google Email */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700">Google Email <span className="text-slate-400 font-normal">(optional)</span></Label>
              <Input type="email" value={form.google_email} onChange={e => setForm({...form, google_email: e.target.value})} placeholder="user@gmail.com" data-testid="google-email-input" className="h-10 border-slate-200" />
              <p className="text-xs text-slate-400">If set, this user can sign in with Google</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-user-btn">
              {saving ? "Saving..." : editMode ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ MAIN PAGE ============

export default function UserManagement() {
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("companies");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes, campRes, pubRes] = await Promise.all([
        adminApi.getPartners(),
        adminApi.getCompanies(),
        adminApi.getCampaigns(),
        adminApi.getPublishers(),
      ]);
      setPartners(pRes.data);
      setCompanies(cRes.data);
      setCampaigns(campRes.data);
      setPublishers(pubRes.data);
    } catch (err) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="user-management-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          User Management
        </h1>
        <p className="text-sm text-slate-400 mt-1">Manage companies, users, and revenue share configuration</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4" data-testid="user-mgmt-tabs">
          <TabsTrigger value="companies" data-testid="companies-tab" className="gap-1.5">
            <Building2 size={14} /> Companies ({companies.length})
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="users-tab" className="gap-1.5">
            <Users size={14} /> Users ({partners.length})
          </TabsTrigger>
        </TabsList>
        <TabsContent value="companies">
          <CompaniesTab companies={companies} campaigns={campaigns} publishers={publishers} onReload={loadData} />
        </TabsContent>
        <TabsContent value="users">
          <UsersTab partners={partners} companies={companies} onReload={loadData} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
